import { useState, useEffect } from "react";
import { useRegister } from "./RegisterContext";
import { toast } from "react-toastify";

/* ── Inline SVG icons ──────────────────────────────── */
const Icon = ({ d, size = 18, stroke = 1.8 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const IconDoorClosed = () => <Icon d={["M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2","M17 21v-8H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h10z"]} />;
const IconCash = () => <Icon d={["M12 1v22","M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"]} />;
const IconCard = () => <Icon d={["M2 7h20v14H2z","M2 11h20"]} />;
const IconTarget = () => <Icon d={["M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z","M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z","M12 12h.01"]} />;
const IconArrowOut = () => <Icon d={["M12 15V3","M7 8l5-5 5 5","M5 21h14"]} />;

const fmt = (n) => `${Number(n || 0).toFixed(3)} TND`;

/* ── Component-specific CSS (scoped via unique ID) ──── */
const CLOSE_REGISTER_CSS = `
  .cr-modal-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.65);
    display: flex; align-items: center; justify-content: center; z-index: 200;
    backdrop-filter: blur(4px);
  }
  .cr-modal {
    background: #151827;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 20px;
    width: 480px;
    max-width: 95vw;
    box-shadow: 0 32px 80px rgba(0,0,0,0.7);
    overflow: hidden;
  }
  .cr-header {
    display: flex; align-items: center; gap: 12px;
    padding: 20px 24px 14px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .cr-header-icon {
    width: 40px; height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, #ff4757, #e84118);
    display: flex; align-items: center; justify-content: center;
    color: #fff;
  }
  .cr-header-title {
    font-size: 16px; font-weight: 700; color: #fff;
  }
  .cr-header-sub {
    font-size: 12px; color: rgba(255,255,255,0.4);
    margin-top: 2px;
  }

  .cr-body {
    padding: 18px 24px;
    display: flex; flex-direction: column; gap: 14px;
  }

  .cr-summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .cr-summary-card {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
    padding: 10px 14px;
    display: flex; flex-direction: column; gap: 4px;
  }
  .cr-summary-card__label {
    font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em;
    color: rgba(255,255,255,0.4); font-weight: 600;
  }
  .cr-summary-card__value {
    font-size: 14px; font-weight: 700; color: #e2e4f0;
    display: flex; align-items: center; gap: 4px;
  }
  .cr-summary-card__value svg { color: #8b8ff7; opacity: 0.7; }

  .cr-balance-box {
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 10px;
    padding: 12px 16px;
    display: flex; justify-content: space-between; align-items: center;
  }
  .cr-balance-box span:first-child {
    display: flex; align-items: center; gap: 8px; color: rgba(255,255,255,0.5);
    font-size: 12px;
  }
  .cr-balance-box strong {
    color: #8b8ff7; font-size: 16px;
  }

  .cr-field label {
    display: block;
    font-size: 10px; font-weight: 600; letter-spacing: 0.07em;
    color: rgba(255,255,255,0.3); text-transform: uppercase;
    margin-bottom: 4px;
  }
  .cr-input {
    padding: 10px 14px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.05);
    color: #e2e4f0; font-size: 14px;
    outline: none; width: 100%;
    transition: border-color 0.2s;
  }
  .cr-input:focus { border-color: #6366f1; }
  .cr-input::placeholder { color: rgba(255,255,255,0.2); }
  
  .cr-diff-badge {
    padding: 8px 14px;
    border-radius: 8px;
    font-size: 13px; font-weight: 600; text-align: center;
    margin-top: 8px;
  }
  .cr-diff-badge--neutral { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.4); }
  .cr-diff-badge--success { background: rgba(46,213,115,0.1); color: #2ed573; border: 1px solid rgba(46,213,115,0.3); }
  .cr-diff-badge--warning { background: rgba(255,168,0,0.1); color: #ffa800; border: 1px solid rgba(255,168,0,0.3); }
  .cr-diff-badge--danger  { background: rgba(255,71,87,0.1); color: #ff4757; border: 1px solid rgba(255,71,87,0.3); }

  .cr-actions {
    display: flex; gap: 10px;
    padding: 16px 24px 20px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
  .cr-btn-cancel {
    flex: 1;
    padding: 10px;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.5); font-size: 13px; cursor: pointer;
    transition: border-color 0.15s, color 0.15s;
  }
  .cr-btn-cancel:hover { border-color: rgba(255,255,255,0.2); color: #fff; }
  .cr-btn-confirm {
    flex: 1; padding: 10px;
    border: none; border-radius: 10px;
    background: linear-gradient(135deg, #ff4757, #e84118);
    color: #fff; font-size: 13px; font-weight: 600; cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 6px;
    transition: opacity 0.15s;
  }
  .cr-btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
  .cr-textarea { resize: vertical; min-height: 60px; }
`;

export default function CloseRegisterModal() {
  const { closeModalVisible, setCloseModalVisible, session, closeRegister } = useRegister();
  const [countedAmount, setCountedAmount] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Inject scoped CSS only once
  useEffect(() => {
    const styleId = "close-register-modal-css";
    if (!document.getElementById(styleId)) {
      const styleEl = document.createElement("style");
      styleEl.id = styleId;
      styleEl.textContent = CLOSE_REGISTER_CSS;
      document.head.appendChild(styleEl);
    }
    return () => {};
  }, []);

  if (!closeModalVisible || !session) return null;

  const expected = Number(session.currentCash);
  const counted = parseFloat(countedAmount);
  const difference = !isNaN(counted) ? counted - expected : null;

  const diffLabel = difference === null ? "—"
    : difference === 0 ? "Balanced"
    : difference > 0 ? `Surplus +${difference.toFixed(3)} TND`
    : `Shortage ${difference.toFixed(3)} TND`;

  const diffTone = difference === null ? "neutral"
    : difference === 0 ? "success"
    : difference > 0 ? "warning"
    : "danger";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isNaN(counted) || counted < 0) {
      toast.warn("Enter the physical cash counted");
      return;
    }
    setSubmitting(true);
    try {
      const res = await closeRegister({ countedAmount: counted, note });
      toast.success(`Register closed — ${res.difference === 0 ? "Balanced" : "Discrepancy recorded"}`);
      setCountedAmount(""); setNote("");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not close register");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cr-modal-overlay" onClick={e => e.target === e.currentTarget && setCloseModalVisible(false)}>
      <div className="cr-modal">
        {/* Header */}
        <div className="cr-header">
          <div className="cr-header-icon">
            <IconDoorClosed />
          </div>
          <div>
            <div className="cr-header-title">Close Register</div>
            <div className="cr-header-sub">Review your shift and count physical cash</div>
          </div>
        </div>

        {/* Body */}
        <div className="cr-body">
          {/* Summary grid */}
          <div className="cr-summary-grid">
            <div className="cr-summary-card">
              <span className="cr-summary-card__label">Opening</span>
              <span className="cr-summary-card__value"><IconCash /> {fmt(session.openingBalance)}</span>
            </div>
            <div className="cr-summary-card">
              <span className="cr-summary-card__label">Cash Sales</span>
              <span className="cr-summary-card__value"><IconCash /> {fmt(session.cashSalesTotal)}</span>
            </div>
            <div className="cr-summary-card">
              <span className="cr-summary-card__label">Card Sales</span>
              <span className="cr-summary-card__value"><IconCard /> {fmt(session.cardSalesTotal)}</span>
            </div>
            <div className="cr-summary-card">
              <span className="cr-summary-card__label">Withdrawals</span>
              <span className="cr-summary-card__value" style={{ color: "#ff6b7a" }}>
                <IconArrowOut /> {fmt(session.withdrawalTotal)}
              </span>
            </div>
          </div>

          {/* Expected cash */}
          <div className="cr-balance-box">
            <span><IconTarget /> Expected Cash</span>
            <strong>{fmt(expected)}</strong>
          </div>

          {/* Physical cash input */}
          <div className="cr-field">
            <label>Physical Cash Counted</label>
            <div style={{ display: "flex" }}>
              <input
                type="number" step="0.001" min="0" autoFocus
                className="cr-input"
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: "none" }}
                value={countedAmount}
                onChange={e => setCountedAmount(e.target.value)}
                placeholder="0.000"
              />
              <span style={{
                padding: "10px 14px",
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderLeft: "none",
                borderRadius: "0 10px 10px 0",
                color: "rgba(255,255,255,0.3)",
                fontSize: "11px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center"
              }}>
                TND
              </span>
            </div>
          </div>

          {/* Difference badge */}
          {countedAmount !== "" && (
            <div className={`cr-diff-badge cr-diff-badge--${diffTone}`}>
              {diffLabel}
            </div>
          )}

          {/* Note */}
          <div className="cr-field">
            <label>Closing Note (optional)</label>
            <textarea
              className="cr-input cr-textarea"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Any remarks about this shift…"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="cr-actions">
          <button type="button" className="cr-btn-cancel" onClick={() => setCloseModalVisible(false)}>
            Cancel
          </button>
          <button type="submit" className="cr-btn-confirm" disabled={submitting} onClick={handleSubmit}>
            {submitting ? "Closing…" : "Confirm & Close Shift"}
          </button>
        </div>
      </div>
    </div>
  );
}